import { LaunchArguments } from "@-/com.npmjs.react-native-launch-arguments";
import onetime from "@-/com.npmjs.onetime";

/**
 * Recordings supplied at launch that stand in for the microphone, one popped each time the
 * learner's voice starts being followed:
 *
 *   - launchApp:
 *       arguments:
 *         hanTestSpeechAudio: "/tmp/ni3-hao3.wav|/tmp/ni2-hao4.wav"
 *
 * The canned transcripts (`hanTestSpeechTranscripts`) stand in for what the recognizer hears;
 * these stand in for the voice itself: the learner's voice is followed in the recording
 * (`lib/voice/tone`), and the recognizer transcribes it (with canned transcripts as well, those
 * stand in for the recognizer instead, since a simulator cannot recognize Mandarin). So an E2E run
 * or a simulator, which has no voice to speak with, runs pitch tracking and tone reading on real
 * audio. A simulator app can
 * read files on its Mac by absolute path; on Android, push the WAV to the device first.
 */
const SEPARATOR = "|";

export function parseTestSpeechAudio(raw: unknown): readonly string[] {
  if (typeof raw !== "string" || raw.length === 0) {
    return [];
  }
  return raw
    .split(SEPARATOR)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

// Reading launch arguments throws without the native module (web, tests), and they cannot change
// after launch.
const getConfiguredRecordings = onetime((): readonly string[] => {
  try {
    return parseTestSpeechAudio(
      LaunchArguments.value<{ hanTestSpeechAudio?: unknown }>().hanTestSpeechAudio,
    );
  } catch {
    return [];
  }
});

/** Position in the queue. Module-level so it survives the remounts a quest call goes through. */
let nextRecordingIndex = 0;

/** Whether this build was launched with recordings to play in place of the microphone. */
export function isTestSpeechAudioEnabled(): boolean {
  return getConfiguredRecordings().length > 0;
}

/** The next recording to play in place of the microphone, or null once they have all played. */
export function popTestSpeechAudio(): string | null {
  const recording = getConfiguredRecordings()[nextRecordingIndex];
  if (recording === undefined) {
    return null;
  }
  nextRecordingIndex += 1;
  return recording;
}
